// U5 "The second snooze offers a smaller step". Reaching for the snooze on a card already snoozed
// once shows, above the snooze times: "Want a smaller first step?" A holding reply, a task, time on
// the calendar, or letting it go ("Letting go is a decision too."). No counts and no blame. The
// offer is made once per card: the server keeps whichever answer the owner gives, snoozing again
// included.
import { useQueryClient } from "@tanstack/react-query";
import { CalendarClock, SquareCheck, Feather, MessageSquareReply } from "lucide-react";
import type { CardView } from "@shared/api";
import {
  LET_GO_COPY,
  LET_GO_REASON,
  SMALLER_STEP_TITLE,
  type SmallerStepChoice,
} from "@shared/unstick";
import { acceptCard } from "@web/lib/feedQueries";
import { unstickApi } from "@web/lib/unstickApi";
import { Button } from "@web/ui/Button";
import { canHoldReply } from "./holdingReply";
import type { CardController } from "./useCardController";

/** Keeps the owner's answer so the offer isn't made again (never in the way if it fails). */
export async function answerSmallerStep(cardId: string, choice: SmallerStepChoice): Promise<void> {
  await unstickApi.smallerStep(cardId, choice).catch(() => undefined);
}

/** A snooze time picked while the offer was on screen: the answer is "snooze", then the snooze. */
export async function snoozeAfterOffer(
  card: CardView,
  controller: Pick<CardController, "snooze">,
  until: string,
  label: string,
): Promise<void> {
  if (card.smallerStep) await answerSmallerStep(card.id, "snooze");
  await controller.snooze(until, label);
}

export function SmallerStepOffer({
  card,
  controller,
}: {
  card: CardView;
  controller: Pick<
    CardController,
    "holdingReply" | "captureTask" | "makeTime" | "dismiss" | "setSnoozeOpen"
  >;
}) {
  const queryClient = useQueryClient();
  const choose = async (choice: SmallerStepChoice, then: () => unknown) => {
    controller.setSnoozeOpen(false);
    const res = await unstickApi.smallerStep(card.id, choice).catch(() => null);
    if (res) acceptCard(queryClient, res.card);
    await then();
  };
  const holdable = canHoldReply(card);
  return (
    <section
      aria-label={SMALLER_STEP_TITLE}
      data-testid="smaller-step"
      className="flex flex-col gap-2 rounded-xl border border-line bg-paper px-4 py-3"
    >
      <p className="font-medium text-ink">{SMALLER_STEP_TITLE}</p>
      <div className="flex flex-wrap gap-2">
        {holdable ? (
          <Button
            size="sm"
            onPointerDown={(e) => e.preventDefault()}
            onClick={() => void choose("holding_reply", controller.holdingReply)}
          >
            <MessageSquareReply aria-hidden className="size-4" />
            Send a holding reply
          </Button>
        ) : null}
        <Button
          size="sm"
          onPointerDown={(e) => e.preventDefault()}
          onClick={() => void choose("task", controller.captureTask)}
        >
          <SquareCheck aria-hidden className="size-4" />
          Make it a task
        </Button>
        <Button
          size="sm"
          onPointerDown={(e) => e.preventDefault()}
          onClick={() => void choose("make_time", controller.makeTime)}
        >
          <CalendarClock aria-hidden className="size-4" />
          Make time
        </Button>
        <Button
          size="sm"
          variant="ghost"
          onPointerDown={(e) => e.preventDefault()}
          onClick={() => void choose("let_go", () => controller.dismiss(LET_GO_REASON))}
        >
          <Feather aria-hidden className="size-4" />
          Let it go
        </Button>
      </div>
      <p className="text-sm text-muted">{LET_GO_COPY}</p>
    </section>
  );
}
